import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { Phone, MessageCircle, Mail, FileCheck2, ShieldCheck, Globe2, Award, MapPin, Star, ArrowRight, CheckCircle2 } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Services — Translation, Notary, Legalization & Visa | NYC Language Institute Thailand";
const DESC =
  "Full service catalog: certified translation (50+ languages), Notary Public, MFA legalization. Ask us anything — real experts reply within minutes.";

export const Route = createFileRoute("/en/services/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/services", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/services", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/services", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/services", "en")},
      { property: "og:url", content: `${SITE}/en/services` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/services` },
      ...hreflangLinks("/services", "/en/services"),
    ],
    scripts: [speakableSchema(), 
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "NYC Legal Services Catalog",
          itemListElement: SERVICES.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: s.title,
            url: `${SITE}${s.enHref}`,
          })),
        }),
      },
    ],
  }),
  component: EnServicesPage,
});

const SERVICES = [
  {
    icon: FileCheck2,
    title: "Certified Translation",
    desc: "Sworn translations in 50+ languages accepted by Thai MFA, embassies, courts, and overseas authorities.",
    bullets: ["MFA-recognized translator", "ISO 17100 certified", "Legal · medical · technical · financial", "Same-day rush available"],
    priceFrom: "฿500/page",
    thHref: "/services/translation",
    enHref: "/en/services/translation",
  },
  {
    icon: ShieldCheck,
    title: "Notary Public",
    desc: "6 in-house attorneys licensed by Thailand's Lawyers Council. Documents accepted worldwide.",
    bullets: ["Affidavits & declarations", "Apostille-equivalent", "Corporate resolutions", "POA & contracts"],
    priceFrom: "฿800/document",
    thHref: "/services/notary-public",
    enHref: "/en/services/notary-public",
  },
  {
    icon: Globe2,
    title: "NAATI Australia",
    desc: "Certified translator CPN9X0Q4P — translations accepted by DIBP, universities, and Australian courts.",
    bullets: ["Skilled migration (189/190/491)", "Partner & student visas", "University admission", "Direct stamp & seal"],
    priceFrom: "฿800/page",
    thHref: "/services/naati",
    enHref: "/en/services/naati",
  },
  {
    icon: Award,
    title: "MFA Legalization",
    desc: "Direct submission to the Thai Ministry of Foreign Affairs. We handle queue, courier, and follow-up.",
    bullets: ["Standard 3–5 business days", "Express 1 day", "Return courier included", "Track via LINE"],
    priceFrom: "฿800/document",
    thHref: "/services/mfa-legalization",
    enHref: "/en/services/mfa-legalization",
  },
  {
    icon: MapPin,
    title: "Embassy Attestation",
    desc: "Hand-delivery to 40+ embassies in Bangkok. Pre-screening to avoid rejections.",
    bullets: ["China, UAE, Saudi, Qatar", "USA, UK, Germany, France", "Russia, Vietnam, Indonesia", "Document pre-check"],
    priceFrom: "Quote on request",
    thHref: "/services/embassy-legalization",
    enHref: "/en/services/embassy-legalization",
  },
  {
    icon: Star,
    title: "Visa & Work Permit",
    desc: "End-to-end visa services for foreigners in Thailand..",
    bullets: ["Non-B / Work Permit", "LTR · Smart · Elite Card", "DTV (Digital Nomad)", "Marriage · Retirement · PR"],
    priceFrom: "Free consultation",
    thHref: "/services/visa-foreigners-thailand",
    enHref: "/en/services/visa-foreigners-thailand",
  },
];

function EnServicesPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link to="/en" className="text-amber-300 hover:underline">← Back to EN home</Link>
            <span className="text-white/30">·</span>
            <Link to="/services" className="text-white/80 hover:text-amber-300">ภาษาไทย</Link>
          </div>
          <h1 className="mt-4 text-3xl font-extrabold sm:text-5xl">
            Full <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">Service Catalog</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80 sm:text-lg">
            Six core service lines. One trusted partner. Built for embassies, multinationals, and individuals who can't afford a single rejection.
          </p>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <article key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:border-amber-400/40 hover:bg-white/[0.06]">
              <div className="flex items-start gap-3">
                <s.icon className="h-7 w-7 flex-none text-amber-300" />
                <div>
                  <h2 className="text-lg font-bold">{s.title}</h2>
                  <p className="mt-1 text-sm text-white/70">{s.desc}</p>
                </div>
              </div>
              <ul className="mt-4 grid gap-1.5 text-xs text-white/75">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-1.5">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-300" /> {b}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                <span className="text-xs font-semibold text-amber-300">From {s.priceFrom}</span>
                <Link to={s.enHref} className="inline-flex items-center gap-1 text-xs font-bold text-white hover:text-amber-300">
                  Full details <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 px-4 py-12">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-500/5 p-6">
          <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
            <Phone className="h-4 w-4" /> +66 83 249 4999
          </a>
          <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-5 py-3 text-sm font-bold text-white hover:bg-[#05B14C]">
            <MessageCircle className="h-4 w-4" /> LINE @nycli
          </a>
          <LeadFormModal trigger={
            <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
              <Mail className="h-4 w-4" /> Free Quote
            </button>
          } />
        </div>
      </section>
          <ServicePageEnhancer serviceName="NYC Legal" locale="en" />
<RelatedHubGrid locale="en" /></main>
  );
}
